import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import { ILS } from '../utils/salesUtils';
import '../styles/pages/catalog-manager.css';

const API_URL = process.env.REACT_APP_API_BASE_URL || '';

const CAT_META = {
  daycare:  { label: 'Guardería',   icon: '🌸', sized: false },
  hotel:    { label: 'Hotel',       icon: '🏨', sized: true  },
  vaccine:  { label: 'Vacuna',      icon: '💉', sized: false },
  vet:      { label: 'Veterinaria', icon: '🩺', sized: false },
  grooming: { label: 'Estética',    icon: '✂️', sized: false },
};

const CATS = Object.keys(CAT_META);

const emptyForm = (cat) => ({
  name: '',
  price: '',
  sizeS: '',
  sizeM: '',
  sizeL: '',
  sizeXL: '',
  unit: '',
  note: '',
});

export default function CatalogManager() {
  const navigate = useNavigate();
  const { authorizedFetch, role } = useAuth();

  const [cat, setCat]         = useState('daycare');
  const [items, setItems]     = useState({});      // { cat: [...] }
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm]   = useState({});
  const [adding, setAdding]       = useState(false);
  const [addForm, setAddForm]     = useState(emptyForm('daycare'));
  const [saving, setSaving]       = useState(false);
  const [toast, setToast]         = useState('');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authorizedFetch(`${API_URL}/api/catalog/items`);
      const data = await r.json();
      if (Array.isArray(data)) {
        const grouped = {};
        for (const c of CATS) grouped[c] = [];
        for (const it of data) {
          if (grouped[it.cat]) grouped[it.cat].push(it);
        }
        setItems(grouped);
      }
    } catch (err) {
      showToast('Error cargando catálogo');
    } finally {
      setLoading(false);
    }
  }, [authorizedFetch]);

  useEffect(() => { loadItems(); }, [loadItems]);

  // ── Edit ──────────────────────────────────────────────────────────────────
  const startEdit = (item) => {
    setAdding(false);
    setEditingId(item._id);
    setEditForm({
      name:   item.name,
      price:  item.price ?? '',
      sizeS:  item.sizes?.S  ?? '',
      sizeM:  item.sizes?.M  ?? '',
      sizeL:  item.sizes?.L  ?? '',
      sizeXL: item.sizes?.XL ?? '',
      unit:   item.unit  ?? '',
      note:   item.note  ?? '',
      active: item.active,
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditForm({}); };

  const handleSave = async (id) => {
    setSaving(true);
    const sized = CAT_META[cat].sized;
    const payload = {
      name:  editForm.name.trim(),
      unit:  editForm.unit,
      note:  editForm.note,
      active: editForm.active,
    };
    if (sized) {
      payload.sizes = {
        S:  parseFloat(editForm.sizeS)  || 0,
        M:  parseFloat(editForm.sizeM)  || 0,
        L:  parseFloat(editForm.sizeL)  || 0,
        XL: parseFloat(editForm.sizeXL) || 0,
      };
    } else {
      payload.price = parseFloat(editForm.price) || 0;
    }

    try {
      const r = await authorizedFetch(`${API_URL}/api/catalog/items/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error();
      const updated = await r.json();
      setItems(prev => ({
        ...prev,
        [cat]: prev[cat].map(it => it._id === id ? updated : it),
      }));
      setEditingId(null);
      showToast('Guardado');
    } catch {
      showToast('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // ── Toggle active ─────────────────────────────────────────────────────────
  const handleToggleActive = async (item) => {
    try {
      const r = await authorizedFetch(`${API_URL}/api/catalog/items/${item._id}`, {
        method: 'PUT',
        body: JSON.stringify({ active: !item.active }),
      });
      if (!r.ok) throw new Error();
      const updated = await r.json();
      setItems(prev => ({
        ...prev,
        [cat]: prev[cat].map(it => it._id === item._id ? updated : it),
      }));
      showToast(updated.active ? 'Servicio activado' : 'Servicio desactivado');
    } catch {
      showToast('Error');
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este servicio? Esta acción no se puede deshacer.')) return;
    try {
      const r = await authorizedFetch(`${API_URL}/api/catalog/items/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error();
      setItems(prev => ({
        ...prev,
        [cat]: prev[cat].filter(it => it._id !== id),
      }));
      showToast('Eliminado');
    } catch {
      showToast('Error al eliminar');
    }
  };

  // ── Add ───────────────────────────────────────────────────────────────────
  const handleAdd = async () => {
    if (!addForm.name.trim()) return;
    setSaving(true);
    const sized = CAT_META[cat].sized;
    const payload = {
      cat,
      name: addForm.name.trim(),
      unit: addForm.unit,
      note: addForm.note,
    };
    if (sized) {
      payload.sizes = {
        S:  parseFloat(addForm.sizeS)  || 0,
        M:  parseFloat(addForm.sizeM)  || 0,
        L:  parseFloat(addForm.sizeL)  || 0,
        XL: parseFloat(addForm.sizeXL) || 0,
      };
    } else {
      payload.price = parseFloat(addForm.price) || 0;
    }

    try {
      const r = await authorizedFetch(`${API_URL}/api/catalog/items`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error();
      const created = await r.json();
      setItems(prev => ({ ...prev, [cat]: [...prev[cat], created] }));
      setAdding(false);
      setAddForm(emptyForm(cat));
      showToast('Servicio agregado');
    } catch {
      showToast('Error al agregar');
    } finally {
      setSaving(false);
    }
  };

  // ── Seed ──────────────────────────────────────────────────────────────────
  const handleSeed = async () => {
    if (!window.confirm('¿Restablecer el catálogo a los valores por defecto? Se eliminarán todos los cambios.')) return;
    setSaving(true);
    try {
      const r = await authorizedFetch(`${API_URL}/api/catalog/seed`, { method: 'POST' });
      if (!r.ok) throw new Error();
      await loadItems();
      showToast('Catálogo restablecido');
    } catch {
      showToast('Error al restablecer');
    } finally {
      setSaving(false);
    }
  };

  // ── Price display helper ───────────────────────────────────────────────────
  const priceDisplay = (item) => {
    if (CAT_META[item.cat]?.sized && item.sizes) {
      return `S: ${ILS(item.sizes.S || 0)} · M: ${ILS(item.sizes.M || 0)} · L: ${ILS(item.sizes.L || 0)} · XL: ${ILS(item.sizes.XL || 0)}`;
    }
    return ILS(item.price || 0);
  };

  const catItems = items[cat] || [];
  const sized = CAT_META[cat].sized;

  return (
    <div className="ctlg-page">
      <Navigation />

      <div className="ctlg-topbar">
        <button className="ctlg-back" onClick={() => navigate('/settings')}>‹</button>
        <h1>Catálogo de servicios</h1>
      </div>

      {/* Category tabs */}
      <div className="ctlg-tabs">
        {CATS.map(c => (
          <button
            key={c}
            className={`ctlg-tab${cat === c ? ' active' : ''}`}
            onClick={() => { setCat(c); setEditingId(null); setAdding(false); setAddForm(emptyForm(c)); }}
          >
            <span className="ctlg-tab-ic">{CAT_META[c].icon}</span>
            <span className="ctlg-tab-lbl">{CAT_META[c].label}</span>
          </button>
        ))}
      </div>

      {/* Items list */}
      {loading ? (
        <div className="ctlg-hint" style={{ marginTop: 24 }}>Cargando…</div>
      ) : (
        <div className="ctlg-list">
          {catItems.length === 0 && (
            <div className="ctlg-hint" style={{ padding: 20 }}>Sin servicios en esta categoría.</div>
          )}

          {catItems.map(item => (
            <div key={item._id} className="ctlg-item">
              {editingId === item._id ? (
                <div className="ctlg-item-edit">
                  {/* Name */}
                  <div className="ctlg-field">
                    <label>Nombre</label>
                    <input
                      value={editForm.name}
                      onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="Nombre del servicio"
                    />
                  </div>

                  {/* Price */}
                  {sized ? (
                    <>
                      <div className="ctlg-field"><label>Precio por tamaño</label></div>
                      <div className="ctlg-sizes-row">
                        {[
                          { key: 'S',  label: 'S (≤10 kg)'  },
                          { key: 'M',  label: 'M (11–20 kg)' },
                          { key: 'L',  label: 'L (21–35 kg)' },
                          { key: 'XL', label: 'XL (>35 kg)'  },
                        ].map(({ key, label }) => (
                          <div key={key} className="ctlg-size-field">
                            <label>{label}</label>
                            <input
                              type="number"
                              min="0"
                              value={editForm[`size${key}`]}
                              onChange={e => setEditForm(f => ({ ...f, [`size${key}`]: e.target.value }))}
                            />
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="ctlg-field">
                      <label>Precio ($)</label>
                      <input
                        type="number"
                        min="0"
                        value={editForm.price}
                        onChange={e => setEditForm(f => ({ ...f, price: e.target.value }))}
                      />
                    </div>
                  )}

                  {/* Unit & Note */}
                  <div className="ctlg-field">
                    <label>Unidad (ej. / visita)</label>
                    <input
                      value={editForm.unit}
                      onChange={e => setEditForm(f => ({ ...f, unit: e.target.value }))}
                      placeholder="/ visita"
                    />
                  </div>
                  <div className="ctlg-field">
                    <label>Nota (opcional)</label>
                    <input
                      value={editForm.note}
                      onChange={e => setEditForm(f => ({ ...f, note: e.target.value }))}
                      placeholder=""
                    />
                  </div>

                  <div className="ctlg-edit-actions">
                    <button className="ctlg-btn-cancel" onClick={cancelEdit}>Cancelar</button>
                    <button
                      className="ctlg-btn-save"
                      disabled={!editForm.name.trim() || saving}
                      onClick={() => handleSave(item._id)}
                    >
                      {saving ? 'Guardando…' : 'Guardar'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="ctlg-item-view">
                  <div className="ctlg-item-info">
                    <div className={`ctlg-item-name${item.active ? '' : ' inactive'}`}>
                      {item.name}
                    </div>
                    <div className="ctlg-item-price">
                      {priceDisplay(item)}
                      {item.unit && <span className="ctlg-item-unit">{item.unit}</span>}
                    </div>
                    {item.note && <div className="ctlg-item-note">{item.note}</div>}
                  </div>
                  <div className="ctlg-item-actions">
                    <button
                      className="ctlg-btn-toggle"
                      onClick={() => handleToggleActive(item)}
                      title={item.active ? 'Desactivar' : 'Activar'}
                    >
                      {item.active ? '✓' : '○'}
                    </button>
                    <button className="ctlg-btn-edit" onClick={() => startEdit(item)}>
                      Editar
                    </button>
                    <button className="ctlg-btn-del" onClick={() => handleDelete(item._id)}>
                      🗑️
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add form */}
      <div className="ctlg-add-wrap">
        {adding ? (
          <div className="ctlg-add-form">
            <h4>Nuevo servicio — {CAT_META[cat].icon} {CAT_META[cat].label}</h4>

            <div className="ctlg-field">
              <label>Nombre</label>
              <input
                value={addForm.name}
                onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Nombre del servicio"
                autoFocus
              />
            </div>

            {sized ? (
              <>
                <div className="ctlg-field"><label>Precio por tamaño</label></div>
                <div className="ctlg-sizes-row">
                  {[
                    { key: 'S',  label: 'S (≤10 kg)'  },
                    { key: 'M',  label: 'M (11–20 kg)' },
                    { key: 'L',  label: 'L (21–35 kg)' },
                    { key: 'XL', label: 'XL (>35 kg)'  },
                  ].map(({ key, label }) => (
                    <div key={key} className="ctlg-size-field">
                      <label>{label}</label>
                      <input
                        type="number"
                        min="0"
                        value={addForm[`size${key}`]}
                        onChange={e => setAddForm(f => ({ ...f, [`size${key}`]: e.target.value }))}
                      />
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="ctlg-field">
                <label>Precio ($)</label>
                <input
                  type="number"
                  min="0"
                  value={addForm.price}
                  onChange={e => setAddForm(f => ({ ...f, price: e.target.value }))}
                />
              </div>
            )}

            <div className="ctlg-field">
              <label>Unidad (ej. / visita)</label>
              <input
                value={addForm.unit}
                onChange={e => setAddForm(f => ({ ...f, unit: e.target.value }))}
                placeholder="/ visita"
              />
            </div>
            <div className="ctlg-field">
              <label>Nota (opcional)</label>
              <input
                value={addForm.note}
                onChange={e => setAddForm(f => ({ ...f, note: e.target.value }))}
              />
            </div>

            <div className="ctlg-edit-actions">
              <button className="ctlg-btn-cancel" onClick={() => { setAdding(false); setAddForm(emptyForm(cat)); }}>
                Cancelar
              </button>
              <button
                className="ctlg-btn-save"
                disabled={!addForm.name.trim() || saving}
                onClick={handleAdd}
              >
                {saving ? 'Guardando…' : 'Agregar'}
              </button>
            </div>
          </div>
        ) : (
          <button className="ctlg-btn-add" onClick={() => { setAdding(true); setEditingId(null); }}>
            <span>＋</span> Agregar servicio
          </button>
        )}
      </div>

      {/* Footer — admin only */}
      {role === 'admin' && (
        <div className="ctlg-footer">
          <button className="ctlg-btn-seed" disabled={saving} onClick={handleSeed}>
            ↺ Restablecer catálogo a valores por defecto
          </button>
          <div className="ctlg-hint">Esto elimina todos los cambios y restaura los precios originales.</div>
        </div>
      )}

      {toast && <div className="ctlg-toast">{toast}</div>}
    </div>
  );
}
